import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/Button";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogDescription,
	DialogTitle,
} from "@/components/catalyst/Dialog";
import { Field, Label } from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { orpc } from "@/orpc/client";

interface RenamePaymentMethodDialogProps {
	isOpen: boolean;
	onClose: () => void;
	entityId: string | null;
	currentName: string;
}

export function RenamePaymentMethodDialog({
	isOpen,
	onClose,
	entityId,
	currentName,
}: RenamePaymentMethodDialogProps) {
	const queryClient = useQueryClient();
	const [name, setName] = useState(currentName);

	// Reset name when dialog opens with new entity
	if (isOpen && name !== currentName && entityId) {
		setName(currentName);
	}

	const { mutate: rename, isPending } = useMutation(
		orpc.billingEntities.rename.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: orpc.billingEntities.list.key(),
				});
				toast.success("Payment method renamed");
				onClose();
			},
			onError: (error) => {
				toast.error(`Failed to rename: ${error.message}`);
			},
		}),
	);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!entityId || !name.trim()) return;
		rename({ id: entityId, name: name.trim() });
	};

	return (
		<Dialog open={isOpen} onClose={onClose}>
			<DialogTitle>Rename Payment Method</DialogTitle>
			<DialogDescription>
				Give this payment method a new name to help you identify it.
			</DialogDescription>
			<form onSubmit={handleSubmit}>
				<DialogBody>
					<Field>
						<Label>Name</Label>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="e.g., Company Card"
							autoFocus
						/>
					</Field>
				</DialogBody>
				<DialogActions>
					<Button plain onClick={onClose} type="button">
						Cancel
					</Button>
					<Button type="submit" disabled={isPending || !name.trim()}>
						{isPending ? "Saving..." : "Save"}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}
